import { enumLabel } from './enumLabel'
import type { Gender } from '@/types/api'

/**
 * Legal gender is wire-encoded as the DB `gender_type` enum — the single-letter
 * codes M/F/O/U — across create, read, and update. The frontend `Gender` type
 * carries those codes verbatim (no parallel `male`/`female` encoding).
 *
 * This module is the ONE place that maps a code to a human label. UI labels
 * live under `patient:demographics.legalGender.values.<word>`
 * (male/female/other/unknown), so we translate code → word-key here and defer
 * to `enumLabel` for the lookup.
 */
const GENDER_CODE_TO_KEY: Record<string, string> = {
  M: 'male',
  F: 'female',
  O: 'other',
  U: 'unknown',
}

/** Canonical ordered list of legal-gender codes, for building <select> options. */
export const GENDER_CODES = ['M', 'F', 'O', 'U'] as const satisfies readonly Gender[]

type TFunctionLike = (
  key: string,
  options?: { defaultValue?: string },
) => string

/**
 * Resolve a legal-gender wire code (M/F/O/U) to its localized label.
 * Returns the raw input unchanged for an unknown code (defensive; the wire
 * value is constrained to the four codes by the DB enum + Zod schema).
 */
export function genderLabel(t: TFunctionLike, code: string): string {
  const word = GENDER_CODE_TO_KEY[code] ?? code
  return enumLabel(t, 'patient', 'demographics.legalGender.values', word)
}
